import {
  Actions,
  Box,
  ContentBlock,
  Heading,
  Stack,
  Text,
  Tiles,
  TooltipRenderer,
} from 'braid-design-system';
import type { ComponentProps, ReactNode } from 'react';

import type { Partner } from 'src/types';
import { makePartnerImageUrl } from 'src/utils/partners/makePartnerImageUrl';

import { ButtonLink, Link } from '../Link/Link';

import * as styles from './PartnerIconListing.css';

interface Props {
  heading?: string;
  text?: ReactNode;
  columns: ComponentProps<typeof Tiles>['columns'];
  limit?: number;
  size?: 'small' | 'standard';
  data?: Partner[];
  cta?: {
    text: string;
    link: string;
    tracking: string;
  };
}

export const PartnerIconListing = ({
  heading,
  text,
  columns,
  limit,
  size = 'standard',
  data,
  cta,
}: Props) => (
  <ContentBlock width="large">
    <Box paddingX="gutter" width="full">
      <Stack space="xlarge" align="center">
        <ContentBlock>
          <Stack space="large" align="center">
            <Heading level="2">{heading}</Heading>
            {text ? (
              <Box paddingX={['none', 'xxlarge']}>
                <Text size="large" align="center" tone="secondary">
                  {text}
                </Text>
              </Box>
            ) : null}
          </Stack>
        </ContentBlock>
        <Tiles columns={columns} space="large">
          {data?.map((item, index) => {
            if (limit && index >= limit) {
              return null;
            }

            return (
              <TooltipRenderer
                key={index}
                id={`${item.name}-tooltip`}
                tooltip={<Text>{item.name}</Text>}
              >
                {({ triggerProps }) => (
                  <Link
                    href={`/connected-partners?name=${item.name.replace(
                      /\+/g,
                      '%2b',
                    )}`}
                    tracking="connected-partners-link-clicked"
                  >
                    <Box
                      aria-label={item.name}
                      {...triggerProps}
                      className={
                        size === 'small'
                          ? styles.partnerIconSmall
                          : styles.partnerIcon
                      }
                      style={{
                        backgroundImage: `url(${makePartnerImageUrl(
                          item.imageUrl,
                        )})`,
                      }}
                    />
                  </Link>
                )}
              </TooltipRenderer>
            );
          })}
        </Tiles>
        {cta ? (
          <Box paddingTop="large">
            <Actions>
              <ButtonLink href={cta?.link} tracking={cta?.tracking}>
                {cta?.text}
              </ButtonLink>
            </Actions>
          </Box>
        ) : null}
      </Stack>
    </Box>
  </ContentBlock>
);
